import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, DollarSign, TrendingUp, Users, Mail, Lock, AlertTriangle, RefreshCw, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'sp_admin_pwd';

const fmtCAD = (n) => new Intl.NumberFormat('fr-CA', {
  style: 'currency', currency: 'CAD', maximumFractionDigits: 0,
}).format(n || 0);

export default function AdminDashboard() {
  const [password, setPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [pwdInput, setPwdInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function tryAuto() {
      if (!password) { setAuthLoading(false); return; }
      try {
        const r = await fetch(`${API}/admin/auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        });
        if (cancelled) return;
        if (r.ok) { setAuthed(true); await loadData(password); }
        else { sessionStorage.removeItem(STORAGE_KEY); setPassword(''); }
      } catch {
        /* keep on login screen */
      } finally {
        if (!cancelled) setAuthLoading(false);
      }
    }
    tryAuto();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (pwd) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/dashboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd }),
      });
      if (!r.ok) throw new Error('dashboard_failed');
      setData(await r.json());
    } catch {
      toast.error('Erreur de chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const r = await fetch(`${API}/admin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwdInput }),
      });
      if (!r.ok) { setAuthError('Mot de passe invalide'); return; }
      sessionStorage.setItem(STORAGE_KEY, pwdInput);
      setPassword(pwdInput);
      setAuthed(true);
      await loadData(pwdInput);
    } catch {
      setAuthError('Erreur réseau');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword(''); setPwdInput(''); setAuthed(false); setData(null);
  };

  // LOGIN
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAF7F2' }}>
        <Loader2 className="animate-spin text-[#7A1F2B]" size={36} />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FAF7F2' }}>
        <div className="w-full max-w-sm bg-white rounded-2xl p-6 sm:p-8 border-2" style={{ borderColor: '#E5DCC9' }}>
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl mx-auto mb-4" style={{ background: '#7A1F2B' }}>
            <Lock className="text-white" size={24} />
          </div>
          <h1 className="font-heading text-2xl font-black text-center mb-2" style={{ color: '#7A1F2B' }}>
            Tableau de bord
          </h1>
          <p className="text-sm text-gray-500 text-center mb-6">SoumissionPro — Accès restreint</p>
          <form onSubmit={handleLogin}>
            <input
              data-testid="dashboard-password-input"
              type="password"
              autoFocus
              required
              value={pwdInput}
              onChange={(e) => setPwdInput(e.target.value)}
              className="field w-full mb-3"
              placeholder="Mot de passe administrateur"
            />
            {authError && (
              <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{authError}</span>
              </div>
            )}
            <button
              data-testid="dashboard-login-btn"
              type="submit"
              className="btn-primary w-full"
            >
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }

  // DASHBOARD
  const subs = data?.subscriptions || {};
  const rev = data?.revenue || {};
  const growth = data?.growth || {};
  const byStatus = subs.by_status || {};
  const byPlan = subs.by_plan || {};

  return (
    <div className="min-h-screen" style={{ background: '#FAF7F2' }}>
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between border-b bg-white" style={{ borderColor: '#E5DCC9' }}>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: '#C9A961' }}>
            ADMINISTRATION
          </div>
          <h1 className="font-heading text-xl sm:text-2xl font-black" style={{ color: '#7A1F2B' }}>
            Tableau de bord business
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="dashboard-refresh"
            onClick={() => loadData(password)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 h-10 rounded-lg border-2 text-sm font-bold disabled:opacity-50"
            style={{ borderColor: '#E5DCC9', color: '#7A1F2B' }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-3 h-10 rounded-lg border-2 text-sm font-bold"
            style={{ borderColor: '#E5DCC9', color: '#7A1F2B' }}
          >
            <LogOut size={14} /> Sortir
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 sm:p-8 space-y-6">
        {/* Nav sub-tabs */}
        <nav className="flex gap-2 text-sm">
          <Link to="/admin/dashboard" className="px-4 h-10 inline-flex items-center rounded-xl font-bold text-white" style={{ background: '#7A1F2B' }}>
            Tableau de bord
          </Link>
          <Link to="/admin/waitlist" className="px-4 h-10 inline-flex items-center rounded-xl font-bold border-2" style={{ borderColor: '#E5DCC9', color: '#7A1F2B' }}>
            Liste d'attente
          </Link>
        </nav>

        {loading && !data ? (
          <div className="p-12 text-center">
            <Loader2 className="animate-spin text-[#7A1F2B] mx-auto" size={36} />
          </div>
        ) : (
          <>
            {/* TOP — Revenue */}
            <section>
              <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2">Revenus</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <BigCard
                  testId="card-mrr"
                  label="MRR (revenus récurrents mensuels)"
                  value={fmtCAD(rev.mrr_cad)}
                  icon={<DollarSign size={20} />}
                  accent="#7A1F2B"
                  highlight
                />
                <BigCard
                  testId="card-yearly"
                  label="Projection annuelle"
                  value={fmtCAD(rev.estimated_yearly_cad)}
                  icon={<TrendingUp size={20} />}
                  accent="#C9A961"
                />
                <BigCard
                  testId="card-lifetime"
                  label="Revenu actif estimé"
                  value={fmtCAD(rev.lifetime_active_cad)}
                  icon={<DollarSign size={20} />}
                  accent="#7A1F2B"
                />
              </div>
            </section>

            {/* Subscriptions */}
            <section>
              <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2">Abonnements</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SmallCard testId="card-active" label="Actifs payants" value={byStatus.active || 0} accent="#16A34A" />
                <SmallCard testId="card-trialing" label="En essai 3 jours" value={byStatus.trialing || 0} accent="#C9A961" />
                <SmallCard testId="card-pastdue" label="Paiement en retard" value={byStatus.past_due || 0} accent="#DC2626" />
                <SmallCard testId="card-canceled" label="Annulés" value={byStatus.canceled || 0} accent="#6B7280" />
              </div>
            </section>

            {/* Plan breakdown + growth */}
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Panel title="Répartition par plan" testId="panel-plans">
                <Row label="Mensuel (29,99 $/mois)" value={byPlan.monthly || 0} />
                <Row label="Annuel (265 $/an)" value={byPlan.annual || 0} />
              </Panel>
              <Panel title="Croissance" testId="panel-growth">
                <Row label="Nouveaux ce mois" value={growth.new_this_month || 0} />
                <Row label="Nouveaux cette semaine" value={growth.new_this_week || 0} />
                <Row
                  label="Taux de conversion estimé"
                  value={`${data?.conversion_rate_pct || 0} %`}
                  hint="(actifs ÷ actifs + essais)"
                />
              </Panel>
            </section>

            {/* Waitlist */}
            <section>
              <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2">Marketing</div>
              <div className="bg-white rounded-2xl border-2 p-5 flex items-center justify-between" style={{ borderColor: '#E5DCC9' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#7A1F2B' }}>
                    <Users className="text-white" size={22} />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500">
                      Liste d'attente App Store
                    </div>
                    <div className="text-3xl font-black" style={{ color: '#7A1F2B' }} data-testid="card-waitlist">
                      {data?.waitlist_total || 0}
                    </div>
                  </div>
                </div>
                <Link
                  to="/admin/waitlist"
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-xl font-bold text-white text-sm"
                  style={{ background: '#7A1F2B' }}
                >
                  <Mail size={14} /> Gérer
                </Link>
              </div>
            </section>

            <div className="text-xs text-gray-400 text-center pt-4">
              Données actualisées le {data?.generated_at ? new Date(data.generated_at).toLocaleString('fr-CA') : '—'}
              <br />
              Le MRR est calculé à partir des abonnements <i>active</i> uniquement (mensuel 29,99 $ + annuel ÷ 12).
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function BigCard({ label, value, icon, accent, highlight, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl p-5 border-2"
      style={{
        borderColor: highlight ? accent : '#E5DCC9',
        background: highlight ? accent : '#FFFFFF',
        color: highlight ? '#FFFFFF' : '#1a1a1a',
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{
            background: highlight ? '#FFFFFF22' : '#FAF7F2',
            color: highlight ? '#FFFFFF' : accent,
          }}
        >
          {icon}
        </div>
        <div className={`text-[11px] font-bold uppercase tracking-widest ${highlight ? 'text-white/80' : 'text-gray-500'}`}>
          {label}
        </div>
      </div>
      <div className="text-3xl sm:text-4xl font-black mt-1" style={{ color: highlight ? '#FFFFFF' : accent }}>
        {value}
      </div>
    </div>
  );
}

function SmallCard({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl p-4 bg-white border-2"
      style={{ borderColor: '#E5DCC9' }}
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</div>
      <div className="text-2xl sm:text-3xl font-black mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Panel({ title, children, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-2xl border-2 p-5" style={{ borderColor: '#E5DCC9' }}>
      <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value, hint }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b last:border-b-0" style={{ borderColor: '#F0E8D9' }}>
      <div className="text-sm text-gray-700">
        {label}
        {hint && <span className="block text-[10px] text-gray-400">{hint}</span>}
      </div>
      <div className="text-lg font-black" style={{ color: '#7A1F2B' }}>{value}</div>
    </div>
  );
}
